import { ReactNode } from 'react'
import { renderElement } from '../document'
import { BoxConstraints, RenderBox } from '../rendering'

type WrapProps = { spacing?: number; runSpacing?: number }

/**
 * Flutter Wrap's horizontal, start-aligned layout. Each child receives the
 * whole available width, not an equal share. Children keep their measured
 * sizes and move intact to the next run when the current run is full.
 * A child wider than one run must handle its own bounded width (e.g. Text).
 */
class WrapRenderBox extends RenderBox<WrapProps> {
  performLayout(c: BoxConstraints) {
    const { spacing = 0, runSpacing = 0 } = this.props
    let x = 0
    let y = 0
    let runHeight = 0
    let width = 0
    let inRun = 0
    for (const child of this.children) {
      child.performLayout({ minWidth: 0, maxWidth: c.maxWidth, minHeight: 0, maxHeight: Infinity })
      let gap = 0
      if (inRun > 0) gap = spacing
      if (inRun > 0 && x + gap + child.box.width > c.maxWidth) {
        y += runHeight + runSpacing
        x = 0
        runHeight = 0
        inRun = 0
        gap = 0
      }
      child.box.x = x + gap
      child.box.y = y
      x = child.box.x + child.box.width
      runHeight = Math.max(runHeight, child.box.height)
      width = Math.max(width, x)
      inRun += 1
    }
    this.box = { x: 0, y: 0, ...BoxConstraints.constrain(c, { width, height: y + runHeight }) }
  }
}

export function Wrap({
  children,
  spacing = 0,
  runSpacing = 0
}: WrapProps & { children: ReactNode }) {
  return renderElement(new WrapRenderBox({ spacing, runSpacing }), children)
}
